/**
 * InstallPrompt - PWA install banner for mobile and desktop
 *
 * Shows a dismissible banner when the app can be installed.
 * Uses the `beforeinstallprompt` browser event.
 * Remembers dismissal for 7 days via localStorage.
 */

import React, { useState, useEffect } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check dismissal timestamp
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) return;

    // iOS detection (no beforeinstallprompt on Safari)
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsIOS(isiOS);
    if (isiOS) {
      setShowBanner(true);
      return;
    }

    // Chrome/Edge/etc — listen for install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!showBanner) return null;

  return (
    <div style={styles.banner}>
      <span style={styles.text}>
        {isIOS ? (
          <>Tap <strong>Share</strong> → <strong>Add to Home Screen</strong> to install</>
        ) : (
          <>Install <strong>My ClinicCore</strong> for quick access</>
        )}
      </span>
      <div style={styles.actions}>
        {!isIOS && (
          <button onClick={handleInstall} style={styles.installButton}>
            Install
          </button>
        )}
        <button onClick={handleDismiss} style={styles.dismissButton}>
          ✕
        </button>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  banner: {
    position: 'fixed',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(22, 50, 91, 0.95)',
    color: '#ffffff',
    borderRadius: '8px',
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    boxShadow: '0 2px 12px rgba(0, 0, 0, 0.25)',
    zIndex: 999,
    maxWidth: '420px',
    width: 'calc(100% - 32px)',
    fontSize: '13px',
    backdropFilter: 'blur(8px)',
  },
  text: {
    flex: 1,
    lineHeight: '1.3',
  },
  actions: {
    display: 'flex',
    gap: '6px',
    flexShrink: 0,
    alignItems: 'center',
  },
  installButton: {
    backgroundColor: '#DBA53A',
    color: '#0A2540',
    border: 'none',
    borderRadius: '4px',
    padding: '5px 12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  },
  dismissButton: {
    backgroundColor: 'transparent',
    color: 'rgba(255,255,255,0.6)',
    border: 'none',
    padding: '4px 6px',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: 1,
  },
};
